import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Dialog } from '@angular/cdk/dialog';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withNavigationErrorHandler } from '@angular/router';
import { MSAL_INSTANCE, MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { MlaAuthService, MlaConfigService, OriginationTokenRefreshService } from '@ld.consumer-portal/data-access';
import { provideTranslocoConfig } from '@ld.consumer-portal/i18n';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import {
  authInterceptor,
  COMPOSE_MESSAGE_LAUNCHER,
  type ComposeMessageLauncherParams,
  customErrorInterceptor,
  GlobalErrorHandler,
  GOOGLE_MAPS_API_KEY_FN,
  ChunkErrorService,
  isChunkLoadingError,
  mockDataInterceptor,
  nativeHttpInterceptor,
  offlineInterceptor,
  SESSION_CLEANUP_FN,
} from '@ld.consumer-portal/utils';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';

function msalInstanceFactory(): IPublicClientApplication {
  const oidcSettings = environment.appSettings.oidcSettings;

  return new PublicClientApplication({
    auth: {
      clientId: oidcSettings?.client_id ?? '',
      authority: oidcSettings?.authority ?? '',
      redirectUri: oidcSettings?.redirect_uri ?? window.location.origin,
      postLogoutRedirectUri: oidcSettings?.post_logout_redirect_uri ?? window.location.origin + '/login',
      knownAuthorities: oidcSettings?.loginHost ? [oidcSettings.loginHost] : [],
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  });
}

// eslint-disable-next-line no-console
console.log('[AppConfig] Environment loaded:', {
  production: environment.production,
  mockEnabled: environment.mockConfig?.enabled,
});

const interceptors = environment.production
  ? [authInterceptor, customErrorInterceptor, offlineInterceptor, nativeHttpInterceptor]
  : [mockDataInterceptor, authInterceptor, customErrorInterceptor, offlineInterceptor, nativeHttpInterceptor];

function isOriginationTemplateBuilderRoute(): boolean {
  return window.location.pathname.startsWith('/template-builder');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      appRoutes,
      withNavigationErrorHandler((navigationError) => {
        if (isChunkLoadingError(navigationError.error)) {
          inject(ChunkErrorService).showRefreshDialog();
        }
      }),
    ),
    provideHttpClient(withInterceptors(interceptors)),
    provideTranslocoConfig(),
    { provide: ENVIRONMENT, useValue: environment },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
    {
      provide: APP_INITIALIZER,
      useFactory: (msalInstance: IPublicClientApplication) => () => msalInstance.initialize(),
      deps: [MSAL_INSTANCE],
      multi: true,
    },
    MsalService,
    MsalBroadcastService,
    {
      provide: SESSION_CLEANUP_FN,
      useFactory: (mlaAuth: MlaAuthService, tokenRefresh: OriginationTokenRefreshService, dialog: Dialog) => () => {
        dialog.closeAll();
        tokenRefresh.stopMonitoring();
        mlaAuth.logout();
      },
      deps: [MlaAuthService, OriginationTokenRefreshService, Dialog],
    },
    {
      provide: GOOGLE_MAPS_API_KEY_FN,
      useFactory: (configService: MlaConfigService) => (partnerId: string) => {
        // The lower-environment builder preview can render address pages without auth.
        // Skip the protected MLA config lookup there and let autocomplete stay disabled.
        if (isOriginationTemplateBuilderRoute()) {
          return Promise.resolve(null);
        }

        return configService.getGoogleMapsApiKey(partnerId);
      },
      deps: [MlaConfigService],
    },
    {
      provide: COMPOSE_MESSAGE_LAUNCHER,
      useFactory: (dialog: Dialog) => {
        return (params?: ComposeMessageLauncherParams) => {
          return new Observable<void>((subscriber) => {
            import('@ld.consumer-portal/documents')
              .then(({ ComposeMessageComponent }) => {
                const dialogRef = dialog.open(ComposeMessageComponent, {
                  hasBackdrop: true,
                  disableClose: true,
                  backdropClass: ['cdk-overlay-dark-backdrop', 'ld-modal-backdrop'],
                  panelClass: ['ld-modal-panel', 'ld-compose-message-panel'],
                  maxWidth: '672px',
                });
                if (params?.category) {
                  dialogRef.componentRef?.setInput('prefillCategory', params.category);
                }
                if (params?.topic) {
                  dialogRef.componentRef?.setInput('prefillTopic', params.topic);
                }
                dialogRef.componentInstance?.closed.subscribe(() => {
                  dialogRef.close();
                });
                dialogRef.closed.subscribe(() => {
                  subscriber.next();
                  subscriber.complete();
                });
              })
              .catch((err) => {
                subscriber.error(err);
              });
          });
        };
      },
      deps: [Dialog],
    },
  ],
};
